import { useTranslation } from 'react-i18next';

import { DefaultWidgetNames, WidgetType } from '@/api/rest/resources/types/widget';
import { WidgetSlotRenderer } from '@/components/widgets/WidgetSlotRenderer';
import { useInSituIndicatorConfigurationOrgWidgetsByDomain } from '@/hooks/useInSituIndicatorConfigurationWidgets';
import { useActiveWidgetSlotConfiguration } from '@/hooks/useWidgetConfigurationByOrganizationId';
import { SignedInPageLayout } from '@/layout/signed-in-page';
import { useOrganizationId } from '@/pages/shared/hooks/useOrganizationId';

import { useMenuItems } from '../../hooks/useMenuItems';

const PAGE_WIDGET_NAME = 'default_template_workstream';
const PAGE_WIDGET_TYPE = WidgetType.t_page;

export const WorkstreamPage = () => {
  const { t } = useTranslation();
  const menuItems = useMenuItems();

  const defaultWidgetConfiguration = useDefaultWidgetConfiguration();
  const customWidgetConfiguration = useActiveWidgetSlotConfiguration(
    PAGE_WIDGET_NAME,
    PAGE_WIDGET_TYPE,
    undefined,
    defaultWidgetConfiguration,
  );
  const widgetConfiguration = customWidgetConfiguration ?? defaultWidgetConfiguration;

  return (
    <SignedInPageLayout
      items={menuItems}
      customizableWidgetTemplateProps={{
        widgetName: PAGE_WIDGET_NAME,
        widgetType: PAGE_WIDGET_TYPE,
        overrideLayoutConfiguration: defaultWidgetConfiguration,
      }}
      navBarProps={{
        title: t('navbar.home'),
        showFilters: true,
      }}
    >
      <div className='grid grid-cols-2 gap-6'>
        <WidgetSlotRenderer nestedWidget={widgetConfiguration} />
      </div>
    </SignedInPageLayout>
  );
};

const useDefaultWidgetConfiguration = () => {
  const organizationId = useOrganizationId();
  const isBiotara = ['70fd9c45-2ae1-4cb6-a99b-41767901cf17'].includes(organizationId);

  const inSituWidgets = useInSituIndicatorConfigurationOrgWidgetsByDomain('workstream');

  const FULL_WIDTH_STYLE = 'col-span-2';

  return {
    name: PAGE_WIDGET_NAME,
    type: PAGE_WIDGET_TYPE,
    widgets: [
      {
        name: 'workstream_overview',
        type: WidgetType.o_c_workstream_overview,
        visible: true,
        relatedData: {
          styles: {
            root: FULL_WIDTH_STYLE,
          },
        },
      },
      {
        name: DefaultWidgetNames.default_plots_map,
        type: WidgetType.o_c_plots_map,
        visible: true,
        relatedData: {
          styles: {
            root: 'col-span-1',
          },
        },
      },
      {
        name: DefaultWidgetNames.default_land_distribution_chart,
        type: WidgetType.o_c_land_distribution_chart,
        visible: true,
      },
      {
        name: DefaultWidgetNames.default_ecosystem_health_chart,
        type: WidgetType.o_c_indicator_health_overview,
        visible: true,
        relatedData: {
          styles: {
            root: FULL_WIDTH_STYLE,
          },
          domainVisibility: {
            carbon: true,
            water: true,
            biodiversity: true,
            soil: true,
            social: true,
          },
        },
      },
      {
        name: DefaultWidgetNames.default_ecological_integrity_decomposition,
        type: WidgetType.o_c_ecological_integrity_decomposition,
        visible: true,
      },
      {
        name: DefaultWidgetNames.default_climate_risk_aggregated_overview,
        type: WidgetType.o_c_risk_aggregated_overview,
        visible: true,
      },
      {
        name: DefaultWidgetNames.default_deforestation_degradation_drivers_decomposition,
        type: WidgetType.o_c_deforestation_degradation_drivers_decomposition,
        visible: isBiotara,
      },
      {
        name: DefaultWidgetNames.default_forest_intelligence_chart,
        type: WidgetType.o_c_plot_group_forest_intelligence_chart,
        visible: isBiotara,
      },
      ...inSituWidgets,
    ],
  };
};
